import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { caseEvidenceApi } from '../../lib/api';
import { apiError } from '../../lib/utils';

const DEFAULT_REASONS = {
  evidence_not_attached: 'Evidence exists but not attached yet',
  extraction_failed: 'Evidence covers items but extraction failed',
  low_risk_exception: 'Low-risk exception approved',
  other: 'Other',
};

export const EvidenceOverrideDialog = ({ open, onOpenChange, caseId, reasons, onDone, testPrefix = 'evidence-override' }) => {
  const [code, setCode] = useState('');
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const opts = reasons || DEFAULT_REASONS;
  const valid = code && note.trim().length >= 5 && (code !== 'other' || other.trim().length >= 3);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await caseEvidenceApi.override(caseId, { reason_code: code, reason_other: code === 'other' ? other : undefined, note });
      toast.success('Evidence override applied');
      onOpenChange(false);
      setCode(''); setOther(''); setNote('');
      onDone?.(r.data);
    } catch (e) {
      toast.error(apiError(e, 'Override failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testPrefix}-dialog`} className="z-[60]">
        <DialogHeader>
          <DialogTitle>Override evidence completeness</DialogTitle>
          <DialogDescription>This lets the case continue with missing evidence. It is logged and raises audit risk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Reason *</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger data-testid={`${testPrefix}-reason`}><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent className="z-[70]">
                {Object.entries(opts).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`${testPrefix}-reason-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {code === 'other' && (
            <div className="space-y-1.5">
              <Label>Other reason *</Label>
              <Input value={other} onChange={(e) => setOther(e.target.value)} data-testid={`${testPrefix}-other`} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Note * (min 5 characters)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testPrefix}-note`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" disabled={!valid || busy} onClick={submit} data-testid={`${testPrefix}-confirm`}>
            Apply override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
